import React, { useEffect, useState } from "react";
import { RiCloseFill, RiDeleteBinLine } from 'react-icons/ri';
import TagInputs from "../layouts/TagInputs";
import SuccessModal from "../modals/SuccessModal";
import UploadButton from "../../Input/UploadButton";
import { get, post, put } from "../../utility/fetch";
import notification from "../../utility/notification";
import TemplateSelector from "../layouts/TemplateSelector";
import DynamicForm from "../layouts/DynamicForm";
import { calculateAge, formatTextareaContent, getFirstName, getLastName } from "../../utility/general";
import { Box, Typography } from "@mui/material";

function ProcedureEditsReportModal({ patientInfo, noDoctor, closeModal, getProcedures, readOnly, walkin }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documentArray, setDocumentArray] = useState([]);
    const [labResults, setLabResults] = useState({});
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labServices, setLabServices] = useState([]);
    const [aiFeedback, setAiFeedback] = useState('');
    const [vitalId, setVitalId] = useState(null);

    // Use the reviewComment field from the patientInfo object
    const reviewComment = Array.isArray(patientInfo?.patientTestReports) ? patientInfo?.patientTestReports[0].reviewComment : null;

    const isMobile = window.innerWidth < 768;

    console.log('Review Comment:', patientInfo);

    const [vitals, setVitals] = useState({
        dateOfVisit: '',
        temperature: '',
        bloodPressure: '',
        oxygenSaturation: '',
        bloodSugar: '',
        heartPulse: '',
        respiratory: '',
        height: '', // in cm
        weight: '', // in kg
        bmi: '',    // calculated
        vitalNurseEmployeeId: 0,
        patientId: patientInfo?.patient?.id || 0,
        appointmentId: patientInfo?.appointmentId || 0,
        labRequestId: patientInfo?.id
    });

    const [payload, setPayload] = useState({
        testReports: [],
        vitalId: 0
    });

    const getVitalId = async () => {
        let res = await get(`/vital/list/appointment/${patientInfo?.appointmentId}`);
        setVitalId(res?.data[0]?.id);
    };


    // Handler for updating vital input fields
    const handleVitalChange = (field) => (e) => {
        if (field === "height" || field === "weight" || field === "heartPulse" || field === "temperature") {
            setVitals(prevVitals => ({
                ...prevVitals,
                [field]: Number(e.target.value),
            }));
        }
        else {
            setVitals(prevVitals => ({
                ...prevVitals,
                [field]: e.target.value,
            }));
        }

    };

    // Effect to calculate BMI whenever height or weight changes
    useEffect(() => {
        if (vitals.height && vitals.weight) {
            const heightInMeters = parseFloat(vitals.height) / 100; // Convert cm to meters
            const weightInKg = parseFloat(vitals.weight);

            if (heightInMeters > 0 && weightInKg > 0) {
                const calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
                setVitals(prevVitals => ({
                    ...prevVitals,
                    bmi: parseFloat(calculatedBmi),
                }));
            } else {
                setVitals(prevVitals => ({ ...prevVitals, bmi: '' }));
            }
        } else {
            setVitals(prevVitals => ({ ...prevVitals, bmi: '' }));
        }
    }, [vitals.height, vitals.weight]);

    // Populate patientId and appointmentId from patientInfo on initial load
    useEffect(() => {
        setVitals(prevVitals => ({
            ...prevVitals,
            patientId: patientInfo?.patient?.id || prevVitals.patientId,
            appointmentId: patientInfo?.appointmentId || prevVitals.appointmentId,
            // Assuming vitalNurseEmployeeId comes from another source (e.g., auth context)
        }));
    }, [patientInfo]);

    const getInternalLabServices = async () => {
        setLoading(true);
        let labServices = patientInfo?.patientTestReports
            // .filter((item) => item?.isReportDone === false)
            .map((request) => request.internalLabService.id)

        console.log(labServices)
        try {
            let res = await get(`/internallabservice/list/${1}/${1000}`);
            setLabServices(
                res?.data?.recordList
                    .filter((item) => labServices.includes(item?.id))
                    .map(item => ({
                        id: item?.id,
                        name: item?.name,
                        fields: item?.labServiceTestDetails?.map(field => ({
                            name: field?.parameter,
                            id: field?.id
                        }))
                    })));
            setLoading(false);
        } catch (error) {
            console.error('Error fetching lab services:', error);
            setLoading(false);
        }
    };

    const handleSave = async (formData) => {
        let vitalData;
        let vitalId = 0;
        if (!walkin) {
            vitalData = await submitVitals();
        }
        if (vitalData) {
            vitalId = vitalData.id;
        }

        try {
            const filteredWalkInRequests = walkin ? getFilteredWalkInTestRequests() : patientInfo?.testRequests || [];

            const testReports = selectedTemplates.map(templateId => {
                const template = labServices.find(t => t.id === templateId);
                return walkin !== true ? {
                    testRequestId: patientInfo?.testRequests?.find(tr => tr.internalLabService.id === templateId)?.id || 0,
                    internalLabServiceId: templateId,
                    labTestServiceResults: template.fields.map(field => ({
                        labServiceTestResultId: field.id || 0,
                        labServiceDetailsId: field.id,
                        result: formData[field.id] || ''
                    })),
                    comment: aiFeedback,
                    patientLabDocuments: documentArray.map(doc => ({
                        docName: doc.name,
                        docPath: doc.path
                    }))
                } :
                    {
                        walkInTestRequestId: filteredWalkInRequests?.find(tr => tr.internalLabService.id === templateId)?.id || 0,
                        internalLabServiceId: templateId,
                        labTestServiceResults: template.fields.map(field => ({
                            labServiceTestResultId: field.id || 0,
                            labServiceDetailsId: field.id,
                            result: formData[field.id] || ''
                        })),
                        comment: aiFeedback,
                        patientLabDocuments: documentArray.map(doc => ({
                            docName: doc.name,
                            docPath: doc.path
                        }))
                    }
            });

            let finalPayload;

            if (walkin !== true) {
                finalPayload = {
                    ...payload,
                    testReports,
                    vitalId: vitalId
                };
            }
            else {
                finalPayload = {
                    ...payload,
                    testReports
                };
            }

            let res;

            if (walkin !== true) {
                // existing non-walkin behaviour
                res = await post("/patientlabreport/internal-lab-report", finalPayload);
            } else {
                // WALK-IN logic: decide POST vs PUT based on isReportDone flags
                // Check isReportDone status only for the SELECTED templates
                const statuses = patientInfo.patientTestReports.map(requests => {
                    // const req = patientInfo.patientTestReports.find(r => r.internalLabService?.id === templateId);
                    return !!requests?.isReportDone; // true if report done, false otherwise
                });

                const anyDone = statuses.some(s => s === true);
                const allDone = statuses.length > 0 && statuses.every(s => s === true);
                const noneDone = statuses.length === 0 || statuses.every(s => s === false);

                console.log({ statuses, anyDone, allDone, noneDone, selectedTemplates });

                if (allDone) {
                    // all selected reports already done — don't re-submit
                    notification({ message: "All selected reports are already marked done.", type: "info" });
                    return;
                }

                // if (anyDone) {
                // Use PUT to update existing walk-in patient test reports
                // Build PUT payload shape as requested (testReportId may be 0 if unknown)
                const putPayload = {
                    testReports: selectedTemplates.map(templateId => {
                        const template = labServices.find(t => t.id === templateId);
                        const matchingRequest = patientInfo.patientTestReports.find(tr => tr.internalLabService?.id === templateId) || {};
                        const matchingRequestResult = matchingRequest?.labTestServiceResults.find(tr => tr?.testDetails?.internalLabServicesId === templateId) || {};
                        // testReportId unknown here – set 0 if not provided by server data
                        return {
                            testReportId: matchingRequest?.id || 0, // use walkInTestRequest.id
                            internalLabServiceId: templateId,
                            labTestServiceResults: template.fields.map(field => ({
                                labServiceTestResultId: matchingRequestResult.id || 0,
                                labServiceDetailsId: field.id,
                                result: formData[field.id] || ''
                            })),
                            comment: aiFeedback,
                            patientLabDocuments: documentArray.map(doc => ({
                                docName: doc.name,
                                docPath: doc.path
                            }))
                        };
                    })
                };


                // patientLabReportId: use patientInfo?.id (walk-in request id)
                const patientLabReportId = patientInfo?.id;
                console.log('PUT payload:', putPayload);
                res = await put(`/walkinpatientlabreport/lab-report/${patientInfo.id}/update-walk-in-patient-test-report`, putPayload);

                // } else if (noneDone) {

                // none done -> create new walk-in lab report (POST)

                // console.log('POST payload:', finalPayload);

                // res = await post("/walkinpatientlabreport/walk-in-lab-report", finalPayload);

                // }
            }


            if (res) {
                notification({
                    message: "Lab Report Successfully Updated",
                    type: "success"
                });
                getProcedures();
                closeModal();
            }
        } catch (error) {
            console.error("Error submitting lab report:", error);
            notification({
                message: "Failed to submit lab report",
                type: "error"
            });
        }
    };




    const submitVitals = async () => {
        try {
            let res = await post("/vital", vitals);

            if (res?.statusCode === 200) {
                notification({
                    type: "success",
                    message: res?.data?.message
                })

                return res?.data;
            }
            else {
                // notification({
                //     type: "error",
                //     message: "There was a problem creating vitals"
                // })
            }
        } catch (error) {
            return;
        }

    }

    const analyzeFile = async (imageFile) => {
        if (!imageFile) {
            alert('Please provide a lab image for analysis.');
            return;
        }

        setLoading(true);
        setAiFeedback('');

        try {
            const formData = new FormData();
            formData.append('ImageToAnalyze', imageFile);
            formData.append('PatientId', 70); // Adjust as needed

            const res = await fetch('https://api.greenzonetechnologies.com.ng/ConnectedHealthWebApi/api/ImageAnalysis/analyze-xray', {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            setAiFeedback(data.data || '✅ AI feedback received.');
        } catch (err) {
            console.error(err);
            setAiFeedback('❌ Error: Failed to analyze image.');
        } finally {
            setLoading(false);
        }
    };

    const deleteDoc = (docName) => {
        setDocumentArray(prev => prev.filter(doc => doc.name !== docName));
    };

    useEffect(() => {
        if (readOnly) {
            getLabResults();
        }
        getInternalLabServices();
        if (walkin !== true) {
            getVitalId();
            setPayload((prev) => ({
                ...prev,
                labRequestId: patientInfo?.id || 0,
                vitalId: vitalId || 0
            }));
        }
        else {
            setPayload((prev) => ({
                ...prev,
                walkInLabRequestId: patientInfo?.id || 0
            }))
        }
    }, []);

    const getLabResults = async () => {
        try {
            let res = await get(`/patientinternallabreport/internal-lab-report/lab-request/${patientInfo?.id}`);
            setLabResults(res[0]);
        } catch (e) {
            console.error(e);
        }
    };

    // New: Filter patientTestReports to only include those with internalLabService.id that exists in labServices
    const getFilteredWalkInTestRequests = () => {
        if (!walkin || !patientInfo?.patientTestReports || labServices.length === 0) {
            return patientInfo?.patientTestReports || [];
        }

        const labServiceIds = labServices.map(service => service.id);
        return patientInfo.patientTestReports.filter(request =>
            request?.internalLabService?.id && labServiceIds.includes(request.internalLabService.id)
        );
    };

    return (
        <div className="overlay">
            <RiCloseFill className='close-btn pointer' onClick={closeModal} />
            <div style={{ width: isMobile ? '95%' : '80%' }} className="modal-content h-80 w-80">
                <div className="w-100 m-t-20">
                    <div className="m-t-10">
                        <h2>Lab Work For {`${patientInfo?.patientFullName || (patientInfo?.walkInPatient?.firstName + ' ' + patientInfo?.walkInPatient?.lastName)}`}</h2>
                        <div className="tabs m-t-10 bold-text"></div>

                        {reviewComment && (
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: '#fffbe6',
                                    border: '1px solid #ffe58f',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: '#d46b08',
                                        mb: 1
                                    }}
                                >
                                    Supervisor's Review Comment
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#595959', whiteSpace: 'pre-wrap' }}>
                                    {reviewComment}
                                </Typography>
                            </Box>
                        )}

                        {/* Patient Information Section */}
                        {
                            walkin && <div className="flex space-between m-l-40 p-20" style={isMobile ? { flexDirection: 'column', marginLeft: 0, padding: '10px' } : {}}>
                                <div className="flex flex-direction-v col-4" style={isMobile ? { width: '100%' } : {}}>
                                    <TagInputs label="Patient's Name" value={`${patientInfo?.firstName} ${patientInfo?.lastName}`} readOnly />
                                    <TagInputs label=" Weight" value={`${getFilteredWalkInTestRequests()[0]?.weight || 'N/A'} Kg`} readOnly />
                                    <TagInputs label="Age" value={`${calculateAge(patientInfo?.dateOfBirth)}`} readOnly />
                                    {!noDoctor && <TagInputs label='Clinic' value={patientInfo?.initiatedHealthCareProvider?.name} readOnly />}
                                    {!noDoctor && <TagInputs label="Doctor's Name" value={`${patientInfo?.doctor?.firstName} ${patientInfo?.doctor?.lastName}`} readOnly />}
                                    <TagInputs label='Date Initiated' value={new Date(patientInfo?.createdAt)?.toLocaleDateString()} readOnly />
                                    <TagInputs label='Time Initiated' value={new Date(patientInfo?.createdAt)?.toLocaleTimeString()} readOnly />
                                </div>
                                <div className="flex flex-direction-v col-8" style={isMobile ? { width: '100%', marginTop: '20px' } : {}}>
                                    {!noDoctor && <TagInputs label="Doctor's Diagnosis" type='textArea' value={patientInfo?.diagnosis} readOnly multiline />}
                                    <TagInputs label='Additional Notes' type='textArea' value={getFilteredWalkInTestRequests()[0]?.additionalNote || ''} readOnly multiline />
                                    <textarea className="h-30" rows={12} disabled value={formatTextareaContent(aiFeedback)} name="" id="" />
                                </div>

                            </div>
                        }

                        {
                            !walkin && <div className="flex space-between m-l-40 p-20" style={isMobile ? { flexDirection: 'column', marginLeft: 0, padding: '10px' } : {}}>
                                <div className="flex flex-direction-v col-4" style={isMobile ? { width: '100%' } : {}}>
                                    <TagInputs label="Patient's Name" value={`${patientInfo?.patient?.firstName} ${patientInfo?.patient?.lastName}`} readOnly />
                                    <TagInputs label=" Weight" value={`${patientInfo?.patient?.weight} Kg`} readOnly />
                                    <TagInputs label="Age" value={`${calculateAge(patientInfo?.patient?.dateOfBirth)}`} readOnly />
                                    {!noDoctor && <TagInputs label='Clinic' value={patientInfo?.initiatedHealthCareProvider?.name} readOnly />}
                                    {!noDoctor && <TagInputs label="Doctor's Name" value={`${patientInfo?.doctor?.firstName} ${patientInfo?.doctor?.lastName}`} readOnly />}
                                    <TagInputs label='Date Initiated' value={new Date(patientInfo?.createdAt)?.toLocaleDateString()} readOnly />
                                    <TagInputs label='Time Initiated' value={new Date(patientInfo?.createdAt)?.toLocaleTimeString()} readOnly />
                                </div>
                                <div className="flex flex-direction-v col-8" style={isMobile ? { width: '100%', marginTop: '20px' } : {}}>
                                    {!noDoctor && <TagInputs label="Doctor's Diagnosis" type='textArea' value={patientInfo?.diagnosis} readOnly multiline />}
                                    <TagInputs label='Additional Notes' type='textArea' value={patientInfo?.additionalNote} readOnly multiline />
                                    {aiFeedback && (
                                        <Box sx={{ p: 2, backgroundColor: '#f0fdf4', borderRadius: 2, borderColor: "green" }}>
                                            <Typography variant="subtitle1">AI Feedback:</Typography>
                                        </Box>
                                    )}
                                    <textarea rows={10} disabled value={formatTextareaContent(aiFeedback)} name="" id="" />
                                </div>
                            </div>
                        }

                        {/* --- Vitals Section --- */}
                        {/* {<div className="vitals-section m-t-20" style={{ marginTop: '20px', padding: '15px', border: '1px solid #eee', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                            <h3 style={{ marginBottom: '15px', color: '#333' }}>Patient Vitals</h3>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Date of Visit"
                                        type="date"
                                        value={vitals.dateOfVisit}
                                        onChange={handleVitalChange('dateOfVisit')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Temperature (°C)"
                                        type="number"
                                        value={vitals.temperature}
                                        onChange={handleVitalChange('temperature')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Blood Pressure (mmHg)"
                                        value={vitals.bloodPressure}
                                        onChange={handleVitalChange('bloodPressure')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Oxygen Saturation (%)"
                                        value={vitals.oxygenSaturation}
                                        onChange={handleVitalChange('oxygenSaturation')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Blood Sugar (mg/dL)"
                                        value={vitals.bloodSugar}
                                        onChange={handleVitalChange('bloodSugar')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Heart Pulse (bpm)"
                                        type="number"
                                        value={vitals.heartPulse}
                                        onChange={handleVitalChange('heartPulse')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Respiratory Rate"
                                        value={vitals.respiratory}
                                        onChange={handleVitalChange('respiratory')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Height (cm)"
                                        type="number"
                                        value={vitals.height}
                                        onChange={handleVitalChange('height')}
                                    />
                                </div>
                            </div>
                            <div className="flex space-between" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="Weight (kg)"
                                        type="number"
                                        value={vitals.weight}
                                        onChange={handleVitalChange('weight')}
                                    />
                                </div>
                                <div className="col-6" style={{ flex: '0 0 48%', marginBottom: '15px' }}>
                                    <TagInputs
                                        label="BMI"
                                        type="number"
                                        value={vitals.bmi}
                                        readOnly // BMI is calculated, not directly editable
                                    />
                                </div>
                            </div>
                        </div>} */}
                        {/* --- End Vitals Section --- */}



                        <div>
                            <h1 className="m-b-20 center-text">Lab Report</h1>
                            <div className="flex m-b-20" style={isMobile ? { flexDirection: 'column' } : {}}>
                                {!readOnly && (
                                    <div className="flex-30" style={isMobile ? { width: '100%', marginBottom: '20px' } : {}}>
                                        {loading ? "Loading Templates..." : (
                                            <TemplateSelector
                                                templates={labServices}
                                                selectedTemplates={selectedTemplates}
                                                setSelectedTemplates={setSelectedTemplates}
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="flex-60" style={isMobile ? { width: '100%' } : {}}>
                                    {!readOnly && selectedTemplates.length > 0 && (
                                        <DynamicForm
                                            labTemplates={labServices}
                                            selectedTemplates={selectedTemplates}
                                            onSubmit={handleSave}
                                        />
                                    )}

                                    {!readOnly && (
                                        <div className="m-t-20 m-b-20">
                                            <Typography sx={{ color: "green" }} variant="h6">
                                                Please Upload laboratory results for AI assisted analysis
                                            </Typography>
                                            <UploadButton
                                                setdocumentArray={setDocumentArray}
                                                analyzeFile={analyzeFile}
                                            />
                                        </div>
                                    )}

                                    {/* Document display */}
                                    {documentArray?.map((doc, idx) => (
                                        <div key={idx} className="m-t-10 flex">
                                            <a href={doc.path} target="_blank" rel="noopener noreferrer" className="m-r-10">
                                                {doc.name}
                                            </a>
                                            <img style={{ width: "200px", height: "200px" }} src={doc.path} alt="" />
                                            <RiDeleteBinLine
                                                color="red"
                                                className="pointer"
                                                onClick={() => deleteDoc(doc.name)}
                                            />
                                        </div>
                                    ))}

                                    {readOnly && labResults?.patientLabDocuments?.map((doc, idx) => (
                                        <div key={idx} className="m-t-10 flex">
                                            <a href={doc.docPath} target="_blank" rel="noopener noreferrer" className="m-r-10">
                                                {doc.docName}
                                            </a>
                                            <img style={{ width: "200px", height: "200px" }} src={doc.docPath} alt="" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProcedureEditsReportModal;